// ═══════════════════════════════════════════════════════════════════════════════
// Admin — single user: summary, login info, payments, reset password, remove
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  AdminGrantEmployerL3TrialResult,
  AdminRemoveAppUserResult,
  AdminUserAccountSummary,
  AdminUserPairingRow,
} from '@fdh/shared';
import { USER_LEVEL, USER_ROLE } from '@fdh/shared';
import { adminApi } from '../../lib/adminApi';
import {
  ADMIN_HKT_DATETIME_PLACEHOLDER,
  hktWallToUtcWall,
} from '../../lib/adminHktDatetime';
import { useLanguage } from '../../contexts/LanguageContext';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { AdminIpWithGeo } from '../../components/admin/AdminIpWithGeo';
import { adminPortalPath } from '../../lib/adminPortalPaths';

type PairingResponse = { pairings: AdminUserPairingRow[] };

const formatMoney = (currency: string, amountCents: number): string => {
  const value = (amountCents / 100).toFixed(2);
  return `${currency} ${value}`;
};

export const AdminUserDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [summary, setSummary] = useState<AdminUserAccountSummary | null>(null);
  const [pairings, setPairings] = useState<AdminUserPairingRow[]>([]);
  const [error, setError] = useState('');
  const [downgradeBusy, setDowngradeBusy] = useState(false);
  const [upgradeBusy, setUpgradeBusy] = useState(false);
  const [grantTrialBusy, setGrantTrialBusy] = useState(false);
  const [downgradeMessage, setDowngradeMessage] = useState('');
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [grantTrialMessage, setGrantTrialMessage] = useState('');
  const [trialEndsAtInput, setTrialEndsAtInput] = useState('');
  const [resetPwdNew, setResetPwdNew] = useState('');
  const [resetPwdConfirm, setResetPwdConfirm] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [pendingReset, setPendingReset] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [removePassword, setRemovePassword] = useState('');
  const [removeReason, setRemoveReason] = useState('');
  const [removeBusy, setRemoveBusy] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(false);
  const [resetSectionOpen, setResetSectionOpen] = useState(false);
  const [removeSectionOpen, setRemoveSectionOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) {
      return;
    }
    setError('');
    try {
      const [sRes, pRes] = await Promise.all([
        adminApi.get<AdminUserAccountSummary>(
          `/admin/users/${encodeURIComponent(id)}/account-summary`,
        ),
        adminApi.get<PairingResponse>(`/admin/users/${encodeURIComponent(id)}/pairing`),
      ]);
      if (sRes.success && sRes.data) {
        setSummary(sRes.data);
        // Admin account-summary datetimes are already HKT (formatViewerDatetime default).
        setTrialEndsAtInput(sRes.data.invitationLevelEndsAt ?? '');
      }
      if (pRes.success && pRes.data?.pairings) {
        setPairings(pRes.data.pairings);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDowngradeToL2 = async () => {
    if (!id || !summary || summary.level !== USER_LEVEL.PAID || summary.role !== USER_ROLE.EMPLOYER) {
      return;
    }
    if (!window.confirm(t('admin.downgradeToL2Confirm'))) {
      return;
    }
    setDowngradeBusy(true);
    setDowngradeMessage('');
    setError('');
    try {
      const res = await adminApi.put<{ archivedTaskIds: string[]; pinMessagesDeactivated: number }>(
        `/admin/users/${encodeURIComponent(id)}/level`,
        { level: USER_LEVEL.FREE },
      );
      if (res.success) {
        setDowngradeMessage(t('admin.downgradeToL2Success'));
        await load();
      } else {
        setError(res.error ?? t('admin.downgradeToL2Error'));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('admin.downgradeToL2Error'));
    } finally {
      setDowngradeBusy(false);
    }
  };

  const handleUpgradeToL3 = async () => {
    if (!id || !summary || summary.level === USER_LEVEL.PAID || summary.role !== USER_ROLE.EMPLOYER) {
      return;
    }
    if (!window.confirm(t('admin.upgradeToL3Confirm'))) {
      return;
    }
    setUpgradeBusy(true);
    setUpgradeMessage('');
    setError('');
    try {
      const res = await adminApi.put<{ employerId: string; fromLevel: number; toLevel: number }>(
        `/admin/users/${encodeURIComponent(id)}/level`,
        { level: USER_LEVEL.PAID },
      );
      if (res.success) {
        setUpgradeMessage(t('admin.upgradeToL3Success'));
        await load();
      } else {
        setError(res.error ?? t('admin.upgradeToL3Error'));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('admin.upgradeToL3Error'));
    } finally {
      setUpgradeBusy(false);
    }
  };

  const handleGrantL3Trial = async () => {
    if (!id || !summary || summary.role !== USER_ROLE.EMPLOYER) {
      return;
    }
    if (!trialEndsAtInput) {
      setError(t('admin.grantL3TrialDateRequired'));
      return;
    }
    if (!window.confirm(t('admin.grantL3TrialConfirm'))) {
      return;
    }
    setGrantTrialBusy(true);
    setGrantTrialMessage('');
    setUpgradeMessage('');
    setDowngradeMessage('');
    setError('');
    try {
      const res = await adminApi.put<AdminGrantEmployerL3TrialResult>(
        `/admin/users/${encodeURIComponent(id)}/grant-l3-trial`,
        { trialEndsAt: hktWallToUtcWall(trialEndsAtInput) },
      );
      if (res.success) {
        setGrantTrialMessage(t('admin.grantL3TrialSuccess'));
        await load();
      } else {
        setError(res.error ?? t('admin.grantL3TrialError'));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('admin.grantL3TrialError'));
    } finally {
      setGrantTrialBusy(false);
    }
  };

  const handleOpenResetDialog = () => {
    const newP = resetPwdNew.trim();
    const c = resetPwdConfirm.trim();
    if (!newP || !c) {
      setError(t('auth.errorGeneric'));
      return;
    }
    if (newP !== c) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    setError('');
    setPendingReset(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!id) {
      return;
    }
    const pwd = resetPwdNew.trim();
    const c = resetPwdConfirm.trim();
    if (!pwd || !c || pwd !== c) {
      setPendingReset(false);
      return;
    }
    setResetBusy(true);
    setError('');
    setResetMessage('');
    try {
      await adminApi.put(`/admin/users/${encodeURIComponent(id)}/reset-password`, {
        newPassword: pwd,
        confirmNewPassword: c,
      });
      setResetPwdNew('');
      setResetPwdConfirm('');
      setResetMessage(t('admin.resetPasswordSuccess'));
      setPendingReset(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
      setPendingReset(false);
    } finally {
      setResetBusy(false);
    }
  };

  const handleOpenRemoveDialog = () => {
    const password = removePassword.trim();
    const reason = removeReason.trim();
    if (!password) {
      setError(t('admin.removeAccountPasswordRequired'));
      return;
    }
    if (reason.length < 1 || reason.length > 500) {
      setError(t('admin.removeAccountReasonInvalid'));
      return;
    }
    setError('');
    setPendingRemove(true);
  };

  const handleConfirmRemove = async () => {
    if (!id) {
      return;
    }
    setRemoveBusy(true);
    setError('');
    try {
      const res = await adminApi.post<AdminRemoveAppUserResult>(
        `/admin/users/${encodeURIComponent(id)}/remove`,
        {
          adminPassword: removePassword.trim(),
          reason: removeReason.trim(),
        },
      );
      if (res.success) {
        setPendingRemove(false);
        navigate(adminPortalPath('users'));
        return;
      }
      setError(res.error ?? t('admin.removeAccountError'));
      setPendingRemove(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('admin.removeAccountError'));
      setPendingRemove(false);
    } finally {
      setRemoveBusy(false);
    }
  };

  if (!id) {
    return null;
  }

  const canDowngrade =
    summary?.role === USER_ROLE.EMPLOYER && summary.level === USER_LEVEL.PAID;
  const canUpgrade =
    summary?.role === USER_ROLE.EMPLOYER &&
    summary.level > 0 &&
    summary.level !== USER_LEVEL.PAID;
  const canGrantTrial = summary?.role === USER_ROLE.EMPLOYER && !summary.isRemoved;

  return (
    <div>
      <Link to={adminPortalPath('users')} className="text-sm text-amber-400 hover:underline">
        ← {t('admin.usersBack')}
      </Link>
      <h1 className="mt-4 text-2xl font-semibold text-white">{t('admin.userDetailTitle')}</h1>

      {error ? (
        <p className="mt-4 text-sm text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      {downgradeMessage ? (
        <p className="mt-4 text-sm text-emerald-400" role="status">
          {downgradeMessage}
        </p>
      ) : null}

      {upgradeMessage ? (
        <p className="mt-4 text-sm text-emerald-400" role="status">
          {upgradeMessage}
        </p>
      ) : null}

      {grantTrialMessage ? (
        <p className="mt-4 text-sm text-emerald-400" role="status">
          {grantTrialMessage}
        </p>
      ) : null}

      {resetMessage ? (
        <p className="mt-4 text-sm text-emerald-400" role="status">
          {resetMessage}
        </p>
      ) : null}

      {summary ? (
        <>
          <section className="mt-6 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
            <h2 className="text-lg font-medium text-white">{t('admin.userSummarySection')}</h2>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-slate-500">{t('admin.email')}</dt>
                <dd className="text-slate-200">{summary.email}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.usersColNickname')}</dt>
                <dd className="text-slate-200">{summary.nickname}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.usersColRole')}</dt>
                <dd className="text-slate-200">{summary.role}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.level')}</dt>
                <dd className="text-slate-200">{summary.levelDisplayName}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.authProvider')}</dt>
                <dd className="text-slate-200">{summary.authProvider}</dd>
              </div>
              {summary.authProvider === 'local' ? (
                <div>
                  <dt className="text-xs text-slate-500">{t('admin.summary.emailVerified')}</dt>
                  <dd className="text-slate-200">
                    {summary.emailVerified ? t('admin.yes') : t('admin.no')}
                  </dd>
                </div>
              ) : null}
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.accountRemovalRequested')}</dt>
                <dd className="text-slate-200">
                  {summary.accountRemovalRequested ? t('admin.yes') : t('admin.no')}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">
                  {t('admin.summary.accountRemovalRequestedAt')}
                </dt>
                <dd className="text-slate-200">{summary.accountRemovalRequestedAt ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.invitationCode')}</dt>
                <dd className="text-slate-200">{summary.invitationCode ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.trialEndsAt')}</dt>
                <dd className="text-slate-200">{summary.trialEndsAt ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.invitationLevelEndsAt')}</dt>
                <dd className="text-slate-200">{summary.invitationLevelEndsAt ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.subscriptionEndsAt')}</dt>
                <dd className="text-slate-200">{summary.subscriptionEndsAt ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.tasks')}</dt>
                <dd className="text-slate-200">{summary.taskCount}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.tasksStandard')}</dt>
                <dd className="text-slate-200">{summary.taskCountByKind.standard}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.tasksInventory')}</dt>
                <dd className="text-slate-200">{summary.taskCountByKind.inventory}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.tasksPickup')}</dt>
                <dd className="text-slate-200">{summary.taskCountByKind.pickup_dropoff}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.tasksMedication')}</dt>
                <dd className="text-slate-200">{summary.taskCountByKind.medication_reminder}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.taskInstances')}</dt>
                <dd className="text-slate-200">{summary.taskInstanceCount}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.receipts')}</dt>
                <dd className="text-slate-200">{summary.receiptCount}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.budgets')}</dt>
                <dd className="text-slate-200">{summary.budgetCount}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.settlements')}</dt>
                <dd className="text-slate-200">{summary.settlementCount}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.family')}</dt>
                <dd className="text-slate-200">{summary.familyMemberCount}</dd>
              </div>
            </dl>

            {canUpgrade || canGrantTrial ? (
              <div className="mt-4 flex flex-col gap-3">
                {canUpgrade ? (
                  <button
                    type="button"
                    onClick={() => {
                      void handleUpgradeToL3();
                    }}
                    disabled={upgradeBusy}
                    tabIndex={0}
                    aria-label={t('admin.upgradeToL3')}
                    className="rounded-lg border border-emerald-600/60 bg-emerald-950/40 px-4 py-2 text-sm font-medium text-emerald-200 hover:bg-emerald-900/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-60"
                  >
                    {upgradeBusy ? t('common.loading') : t('admin.upgradeToL3')}
                  </button>
                ) : null}

                {canGrantTrial ? (
                  <div className="rounded-lg border border-slate-700 bg-slate-950/40 p-3">
                    <p className="text-sm font-medium text-white">{t('admin.grantL3TrialTitle')}</p>
                    <p className="mt-1 text-xs text-slate-400">{t('admin.grantL3TrialHint')}</p>
                    <label className="mt-3 block text-xs text-slate-400" htmlFor="grant-l3-trial-ends-at">
                      {t('admin.grantL3TrialField')}
                    </label>
                    <input
                      id="grant-l3-trial-ends-at"
                      type="text"
                      value={trialEndsAtInput}
                      onChange={(event) => {
                        setTrialEndsAtInput(event.target.value);
                      }}
                      placeholder={ADMIN_HKT_DATETIME_PLACEHOLDER}
                      className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      aria-label={t('admin.grantL3TrialField')}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        void handleGrantL3Trial();
                      }}
                      disabled={grantTrialBusy}
                      tabIndex={0}
                      aria-label={t('admin.grantL3TrialButton')}
                      className="mt-3 rounded-lg border border-cyan-600/60 bg-cyan-950/40 px-4 py-2 text-sm font-medium text-cyan-200 hover:bg-cyan-900/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:opacity-60"
                    >
                      {grantTrialBusy ? t('common.loading') : t('admin.grantL3TrialButton')}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {canDowngrade ? (
              <button
                type="button"
                onClick={() => {
                  void handleDowngradeToL2();
                }}
                disabled={downgradeBusy}
                tabIndex={0}
                aria-label={t('admin.downgradeToL2')}
                className="mt-4 rounded-lg border border-amber-600/60 bg-amber-950/40 px-4 py-2 text-sm font-medium text-amber-200 hover:bg-amber-900/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:opacity-60"
              >
                {downgradeBusy ? t('common.loading') : t('admin.downgradeToL2')}
              </button>
            ) : null}
          </section>

          <section className="mt-8 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
            <h2 className="text-lg font-medium text-white">{t('admin.loginInfoSection')}</h2>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.registeredAt')}</dt>
                <dd className="text-slate-200">{summary.registeredAt}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.registeredIp')}</dt>
                <dd className="text-slate-200">
                  <AdminIpWithGeo ip={summary.registeredIp} geo={summary.registeredIpGeo} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.lastLoginAt')}</dt>
                <dd className="text-slate-200">{summary.lastLoginAt ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.lastLoginIp')}</dt>
                <dd className="text-slate-200">
                  <AdminIpWithGeo ip={summary.lastLoginIp} geo={summary.lastLoginIpGeo} />
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs text-slate-500">{t('admin.summary.lastLoginDevice')}</dt>
                <dd className="break-all text-slate-200">{summary.lastLoginDevice ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.usageRegion')}</dt>
                <dd className="text-slate-200">{summary.usageRegion}</dd>
              </div>
            </dl>
          </section>

          <section className="mt-8 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
            <h2 className="text-lg font-medium text-white">{t('admin.paymentSection')}</h2>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-slate-500">{t('admin.summary.stripeCustomer')}</dt>
                <dd className="text-slate-200">
                  {summary.stripeCustomerUrl ? (
                    <a
                      href={summary.stripeCustomerUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-amber-400 hover:underline"
                      tabIndex={0}
                      aria-label={t('admin.summary.stripeCustomerLink')}
                    >
                      {summary.stripeCustomerId}
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
            </dl>
            {summary.invoices.length === 0 ? (
              <p className="mt-3 text-sm text-slate-400">{t('admin.paymentEmpty')}</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-800 text-left text-sm">
                  <thead className="text-slate-400">
                    <tr>
                      <th className="px-2 py-2 font-medium">{t('admin.paymentColPaidAt')}</th>
                      <th className="px-2 py-2 font-medium">{t('admin.paymentColPeriod')}</th>
                      <th className="px-2 py-2 font-medium">{t('admin.paymentColAmount')}</th>
                      <th className="px-2 py-2 font-medium">{t('admin.paymentColStatus')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {summary.invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="px-2 py-2 text-slate-300">{invoice.createdAt ?? '—'}</td>
                        <td className="px-2 py-2 text-slate-300">
                          {invoice.periodStart ?? '—'} → {invoice.periodEnd ?? '—'}
                        </td>
                        <td className="px-2 py-2 text-slate-300">
                          {formatMoney(
                            invoice.currency,
                            invoice.amountPaid > 0 ? invoice.amountPaid : invoice.amountDue,
                          )}
                        </td>
                        <td className="px-2 py-2 text-slate-300">
                          {invoice.hostedInvoiceUrl ? (
                            <a
                              href={invoice.hostedInvoiceUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-amber-400 hover:underline"
                            >
                              {invoice.status}
                            </a>
                          ) : (
                            invoice.status
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {summary.canResetPassword ? (
            <section className="mt-8 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
              <button
                type="button"
                className="flex w-full items-center justify-between text-left"
                onClick={() => setResetSectionOpen((open) => !open)}
                aria-expanded={resetSectionOpen}
                aria-controls="admin-reset-password-panel"
              >
                <h2 className="text-lg font-medium text-white">{t('admin.usersResetPassword')}</h2>
                <span className="text-sm text-slate-400">
                  {resetSectionOpen ? t('admin.sectionCollapse') : t('admin.sectionExpand')}
                </span>
              </button>
              {resetSectionOpen ? (
                <div id="admin-reset-password-panel">
                  <p className="mt-1 text-xs text-slate-400">{t('admin.resetPasswordLocalOnlyHint')}</p>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
                    <label className="block flex-1 text-xs text-slate-400">
                      {t('admin.usersNewPasswordPlaceholder')}
                      <input
                        type="password"
                        value={resetPwdNew}
                        onChange={(ev) => setResetPwdNew(ev.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                        aria-label={t('admin.usersNewPasswordPlaceholder')}
                      />
                    </label>
                    <label className="block flex-1 text-xs text-slate-400">
                      {t('admin.usersConfirmNewPasswordPlaceholder')}
                      <input
                        type="password"
                        value={resetPwdConfirm}
                        onChange={(ev) => setResetPwdConfirm(ev.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                        aria-label={t('admin.usersConfirmNewPasswordPlaceholder')}
                      />
                    </label>
                    <button
                      type="button"
                      disabled={resetBusy || !resetPwdNew.trim() || !resetPwdConfirm.trim()}
                      className="rounded bg-amber-600 px-4 py-2 text-sm font-medium text-slate-950 hover:bg-amber-500 disabled:opacity-50"
                      onClick={handleOpenResetDialog}
                      tabIndex={0}
                      aria-label={t('admin.usersResetPassword')}
                    >
                      {resetBusy ? t('common.loading') : t('admin.usersResetPassword')}
                    </button>
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}

          {!summary.isRemoved ? (
            <section className="mt-8 rounded-lg border border-red-900/60 bg-red-950/20 p-4">
              <button
                type="button"
                className="flex w-full items-center justify-between text-left"
                onClick={() => setRemoveSectionOpen((open) => !open)}
                aria-expanded={removeSectionOpen}
                aria-controls="admin-remove-account-panel"
              >
                <h2 className="text-lg font-medium text-red-200">{t('admin.removeAccountTitle')}</h2>
                <span className="text-sm text-slate-400">
                  {removeSectionOpen ? t('admin.sectionCollapse') : t('admin.sectionExpand')}
                </span>
              </button>
              {removeSectionOpen ? (
                <div id="admin-remove-account-panel">
                  <p className="mt-1 text-xs text-slate-400">{t('admin.removeAccountHint')}</p>
                  <label className="mt-4 block text-xs text-slate-400" htmlFor="admin-remove-password">
                    {t('admin.removeAccountPassword')}
                  </label>
                  <input
                    id="admin-remove-password"
                    type="password"
                    value={removePassword}
                    onChange={(ev) => setRemovePassword(ev.target.value)}
                    className="mt-1 w-full max-w-md rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                    aria-label={t('admin.removeAccountPassword')}
                  />
                  <label className="mt-3 block text-xs text-slate-400" htmlFor="admin-remove-reason">
                    {t('admin.removeAccountReason')}
                  </label>
                  <textarea
                    id="admin-remove-reason"
                    value={removeReason}
                    onChange={(ev) => setRemoveReason(ev.target.value)}
                    maxLength={500}
                    rows={3}
                    className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                    aria-label={t('admin.removeAccountReason')}
                  />
                  <p className="mt-1 text-xs text-slate-500">{removeReason.trim().length}/500</p>
                  <button
                    type="button"
                    disabled={removeBusy}
                    className="mt-4 rounded-lg border border-red-600/60 bg-red-950/40 px-4 py-2 text-sm font-medium text-red-200 hover:bg-red-900/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:opacity-60"
                    onClick={handleOpenRemoveDialog}
                    tabIndex={0}
                    aria-label={t('admin.removeAccountButton')}
                  >
                    {removeBusy ? t('common.loading') : t('admin.removeAccountButton')}
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}
        </>
      ) : (
        !error && (
          <p className="mt-8 text-sm text-slate-500" role="status">
            {t('common.loading')}
          </p>
        )
      )}

      <section className="mt-8 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
        <h2 className="text-lg font-medium text-white">{t('admin.pairingSection')}</h2>
        {pairings.length === 0 ? (
          <p className="mt-2 text-sm text-slate-400">{t('admin.pairingEmpty')}</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {pairings.map((p) => (
              <li
                key={p.pairingId}
                className="rounded-md border border-slate-800 px-3 py-2 text-sm text-slate-300"
              >
                <div>
                  <span className="text-slate-500">{t('admin.pairingStatus')}:</span> {p.status}{' '}
                  · <span className="text-slate-500">{t('admin.pairingRole')}:</span>{' '}
                  {p.roleInPairing} · {p.pairedAt}
                </div>
                <div className="mt-1">
                  <span className="text-slate-500">{t('admin.pairingPartner')}:</span>{' '}
                  {p.partnerNickname} ({p.partnerEmail})
                </div>
                <Link
                  to={adminPortalPath(`users/${encodeURIComponent(p.partnerId)}`)}
                  className="mt-2 inline-block text-amber-400 hover:underline"
                  tabIndex={0}
                  aria-label={t('admin.pairingPartnerDetail')}
                >
                  {t('admin.pairingPartnerDetail')}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={pendingReset}
        title={t('admin.resetPasswordDialogTitle')}
        description={
          summary ? t('admin.resetPasswordDialogBody', { email: summary.email }) : ''
        }
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleConfirmResetPassword()}
        onCancel={() => setPendingReset(false)}
        variant="danger"
      />

      <ConfirmDialog
        open={pendingRemove}
        title={t('admin.removeAccountDialogTitle')}
        description={
          summary ? t('admin.removeAccountDialogBody', { email: summary.email }) : ''
        }
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleConfirmRemove()}
        onCancel={() => setPendingRemove(false)}
        variant="danger"
      />
    </div>
  );
};
